import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Tag,
  Typography,
  Alert,
  Select,
  DatePicker,
  Button,
  Modal,
  Space,
  Row,
  Col,
  Statistic,
  Progress,
  Tooltip,
  Descriptions,
  Tabs,
} from "antd";
import {
  ReloadOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  OrderedListOutlined,
  TeamOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../../services/api";

const { Title, Text } = Typography;
const { Option } = Select;

// completion/coverage ต่ำกว่านี้ = ไฮไลต์เตือน
const LOW_COMPLETION_THRESHOLD = 80;

const STATUS_TAGS = {
  completed: { color: "green", label: "ตรวจแล้ว" },
  late: { color: "orange", label: "ตรวจช้า" },
  missed: { color: "red", label: "ขาดตรวจ" },
  unable: { color: "gold", label: "ตรวจไม่ได้" },
};

const formatDateTime = (dt) =>
  dt ? dayjs(dt).format("DD/MM/YYYY HH:mm:ss") : "-";

const rateColor = (rate) =>
  rate >= 90 ? "#52c41a" : rate >= LOW_COMPLETION_THRESHOLD ? "#faad14" : "#f5222d";

const GuardPerformance = () => {
  const [activeTab, setActiveTab] = useState("guards");
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState("");

  const [companyId, setCompanyId] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(29, "day"),
    dayjs(),
  ]);

  // ─── Tab 1: per-guard ───────────────────────────────────────────────
  const [guards, setGuards] = useState([]);
  const [guardsLoading, setGuardsLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  // ─── Tab 2: per-round ───────────────────────────────────────────────
  const [rounds, setRounds] = useState([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [roundsLoaded, setRoundsLoaded] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [session, setSession] = useState(null);

  const buildParams = useCallback(() => {
    const params = {
      start_date: dateRange[0].format("YYYY-MM-DD"),
      end_date: dateRange[1].format("YYYY-MM-DD"),
    };
    if (companyId) params.company_id = companyId;
    return params;
  }, [dateRange, companyId]);

  const fetchGuards = useCallback(async () => {
    try {
      setGuardsLoading(true);
      const res = await api.get("/superadmin/guard-performance", {
        params: buildParams(),
      });
      if (res.data.success) {
        setGuards(res.data.data.guards);
        setError("");
      }
    } catch (err) {
      console.error("Failed to fetch guard performance:", err);
      setError("ไม่สามารถโหลดข้อมูลประสิทธิภาพ รปภ. ได้");
    } finally {
      setGuardsLoading(false);
    }
  }, [buildParams]);

  const fetchRounds = useCallback(async () => {
    try {
      setRoundsLoading(true);
      const res = await api.get("/superadmin/round-completeness", {
        params: buildParams(),
      });
      if (res.data.success) {
        setRounds(res.data.data.rounds);
        setRoundsLoaded(true);
        setError("");
      }
    } catch (err) {
      console.error("Failed to fetch round completeness:", err);
      setError("ไม่สามารถโหลดข้อมูลความครบถ้วนรายรอบได้");
    } finally {
      setRoundsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/superadmin/companies");
        if (res.data.success) setCompanies(res.data.data);
      } catch (err) {
        console.error("Failed to fetch companies:", err);
      }
    })();
  }, []);

  // Refetch active tab when filters change; round tab loads lazily on first open
  useEffect(() => {
    fetchGuards();
    setRoundsLoaded(false);
  }, [fetchGuards]);

  useEffect(() => {
    if (activeTab === "rounds" && !roundsLoaded && !roundsLoading) {
      fetchRounds();
    }
  }, [activeTab, roundsLoaded, roundsLoading, fetchRounds]);

  const refresh = () => {
    if (activeTab === "guards") fetchGuards();
    else fetchRounds();
  };

  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    try {
      setExporting(true);
      const params = buildParams();
      const res = await api.get("/superadmin/guard-performance/export/excel", {
        params,
        responseType: "blob",
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `guard-performance_${params.start_date}_${params.end_date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export excel:", err);
      setError("ไม่สามารถ export Excel ได้");
    } finally {
      setExporting(false);
    }
  };

  const openDetail = async (guard) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await api.get(
        `/superadmin/guard-performance/${guard.guard_id}`,
        { params: buildParams() },
      );
      if (res.data.success) setDetail(res.data.data);
    } catch (err) {
      console.error("Failed to fetch guard detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const openSession = async (round, sess) => {
    setSessionOpen(true);
    setSessionLoading(true);
    setSession(null);
    try {
      const res = await api.get(
        `/superadmin/round-completeness/${round.round_id}/detail`,
        { params: { date: sess.date } },
      );
      if (res.data.success) setSession(res.data.data);
    } catch (err) {
      console.error("Failed to fetch session detail:", err);
    } finally {
      setSessionLoading(false);
    }
  };

  // ─── Tab 1 columns ──────────────────────────────────────────────────
  const guardColumns = [
    {
      title: "ชื่อ รปภ.",
      dataIndex: "guard_name",
      key: "guard_name",
      sorter: (a, b) => a.guard_name.localeCompare(b.guard_name, "th"),
      render: (name, record) => (
        <div style={{ minWidth: 140 }}>
          <div>
            <Text strong>{name}</Text>
            {!record.is_active && (
              <Tag color="default" style={{ marginLeft: 8 }}>
                ปิดใช้งาน
              </Tag>
            )}
          </div>
          {record.employee_id && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              รหัส {record.employee_id}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "บริษัทที่เดินตรวจ (ช่วงที่เลือก)",
      dataIndex: "patrol_companies",
      key: "patrol_companies",
      responsive: ["md"],
      render: (list) =>
        list && list.length > 0 ? (
          <Space direction="vertical" size={2}>
            {list.map((name) => (
              <Tag key={name} color="blue" style={{ marginRight: 0 }}>
                {name}
              </Tag>
            ))}
          </Space>
        ) : (
          "-"
        ),
    },
    {
      title: "จุดที่สแกน",
      dataIndex: "total_checks",
      key: "total_checks",
      align: "center",
      sorter: (a, b) => a.total_checks - b.total_checks,
    },
    {
      title: (
        <Tooltip title="สแกนหลังเวลาจบรอบ (ผ่อนผัน 59 วินาที — จบ 13:59 นับถึง 13:59:59)">
          ตรวจช้า
        </Tooltip>
      ),
      dataIndex: "late",
      key: "late",
      align: "center",
      sorter: (a, b) => a.late - b.late,
      render: (v) =>
        v > 0 ? <Text style={{ color: "#fa8c16" }}>{v}</Text> : v,
    },
    {
      title: "% ตรงเวลา",
      dataIndex: "ontime_rate",
      key: "ontime_rate",
      align: "center",
      sorter: (a, b) => a.ontime_rate - b.ontime_rate,
      render: (rate) => (
        <Text style={{ color: rateColor(rate) }}>{rate}%</Text>
      ),
    },
    {
      title: (
        <Tooltip title="จำนวน session (รอบ, วัน) ที่สแกนอย่างน้อย 1 จุด">
          รอบที่ร่วม
        </Tooltip>
      ),
      dataIndex: "rounds_joined",
      key: "rounds_joined",
      align: "center",
      sorter: (a, b) => a.rounds_joined - b.rounds_joined,
    },
    {
      title: "รอบครบ",
      dataIndex: "rounds_complete",
      key: "rounds_complete",
      align: "center",
      render: (v) => <Text style={{ color: "#52c41a" }}>{v}</Text>,
    },
    {
      title: (
        <Tooltip title="รอบที่ร่วมสแกนแต่ทีมสแกนรวมกันไม่ครบทุกจุด">
          รอบขาด
        </Tooltip>
      ),
      dataIndex: "rounds_incomplete",
      key: "rounds_incomplete",
      align: "center",
      sorter: (a, b) => a.rounds_incomplete - b.rounds_incomplete,
      render: (v) =>
        v > 0 ? <Text style={{ color: "#f5222d" }}>{v}</Text> : v,
    },
    {
      title: (
        <Tooltip title="รอบครบ ÷ รอบที่ร่วม — นับผลรวมของทีม ไม่ใช่แค่จุดที่ตัวเองสแกน">
          % รอบครบ
        </Tooltip>
      ),
      dataIndex: "round_complete_rate",
      key: "round_complete_rate",
      align: "center",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.round_complete_rate - b.round_complete_rate,
      render: (rate) => (
        <Progress
          percent={rate}
          size="small"
          strokeColor={rateColor(rate)}
          format={(p) => `${p}%`}
          style={{ minWidth: 90 }}
        />
      ),
    },
    {
      title: "ใช้งานล่าสุด",
      dataIndex: "last_active",
      key: "last_active",
      responsive: ["lg"],
      sorter: (a, b) =>
        new Date(a.last_active || 0) - new Date(b.last_active || 0),
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "-"),
    },
  ];

  const detailLogColumns = [
    {
      title: "วันเวลา",
      dataIndex: "time_scheduled",
      key: "time_scheduled",
      render: formatDateTime,
    },
    {
      title: "บริษัท",
      dataIndex: "company_name",
      key: "company_name",
      responsive: ["md"],
    },
    {
      title: "รอบ",
      dataIndex: "round_name",
      key: "round_name",
      render: (v) => v || "-",
    },
    {
      title: "จุดตรวจ",
      dataIndex: "checkpoint_name",
      key: "checkpoint_name",
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status, record) => {
        const tag = STATUS_TAGS[status] || { color: "default", label: status };
        return (
          <Space size={4}>
            <Tag color={tag.color}>{tag.label}</Tag>
            {record.is_late && status === "completed" && (
              <Tag color="orange">ช้า</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "เวลาสแกน",
      dataIndex: "time_checked",
      key: "time_checked",
      responsive: ["md"],
      render: (v) => (v ? dayjs(v).format("HH:mm:ss") : "-"),
    },
    {
      title: "เหตุผล",
      dataIndex: "reason",
      key: "reason",
      responsive: ["lg"],
      render: (v) => v || "-",
    },
  ];

  const guardSessionColumns = [
    {
      title: "วันที่",
      dataIndex: "date",
      key: "date",
      render: (v) => dayjs(v).format("DD/MM/YYYY"),
    },
    {
      title: "รอบ",
      key: "round",
      render: (_, s) => (
        <div>
          <div>{s.round_name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {s.company_name}
          </Text>
        </div>
      ),
    },
    {
      title: "เขาสแกน",
      dataIndex: "guard_scanned",
      key: "guard_scanned",
      align: "center",
      render: (v) => `${v} จุด`,
    },
    {
      title: "ทีมรวม",
      key: "team",
      align: "center",
      render: (_, s) => (
        <Text>
          {s.session_scanned}/{s.expected}
        </Text>
      ),
    },
    {
      title: "ผลของรอบ",
      key: "result",
      align: "center",
      render: (_, s) => {
        if (s.in_progress) return <Tag color="processing">กำลังดำเนินการ</Tag>;
        if (s.complete) return <Tag color="green">ครบ</Tag>;
        return (
          <Tag color="red">ขาด {Math.max(0, s.expected - s.session_scanned)} จุด</Tag>
        );
      },
    },
  ];

  const detailSummary = detail
    ? detail.daily_trend.reduce(
        (acc, d) => ({
          total: acc.total + d.total_checks,
          scanned: acc.scanned + d.scanned,
          missed: acc.missed + d.missed,
          unable: acc.unable + d.unable,
          late: acc.late + d.late,
        }),
        { total: 0, scanned: 0, missed: 0, unable: 0, late: 0 },
      )
    : null;

  // ─── Tab 2 columns ──────────────────────────────────────────────────
  const roundColumns = [
    {
      title: "รอบตรวจ",
      dataIndex: "round_name",
      key: "round_name",
      render: (name, record) => (
        <div style={{ minWidth: 140 }}>
          <div>
            <Text strong>{name}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.start_time?.slice(0, 5)} - {record.end_time?.slice(0, 5)} ·{" "}
            {record.current_checkpoints} จุด
          </Text>
        </div>
      ),
    },
    {
      title: "บริษัท",
      dataIndex: "company_name",
      key: "company_name",
      responsive: ["md"],
      sorter: (a, b) => a.company_name.localeCompare(b.company_name, "th"),
    },
    {
      title: "จำนวนวัน",
      dataIndex: "days_counted",
      key: "days_counted",
      align: "center",
      sorter: (a, b) => a.days_counted - b.days_counted,
    },
    {
      title: "วันครบ",
      dataIndex: "days_complete",
      key: "days_complete",
      align: "center",
      render: (v) => <Text style={{ color: "#52c41a" }}>{v}</Text>,
    },
    {
      title: "วันขาด",
      dataIndex: "days_incomplete",
      key: "days_incomplete",
      align: "center",
      sorter: (a, b) => a.days_incomplete - b.days_incomplete,
      render: (v) =>
        v > 0 ? <Text style={{ color: "#f5222d" }}>{v}</Text> : v,
    },
    {
      title: (
        <Tooltip title="จุดที่ถูกสแกน (โดยใครก็ได้) เทียบกับจุดทั้งหมดของรอบ รวมทุกวันในช่วง">
          % ความครบถ้วน
        </Tooltip>
      ),
      dataIndex: "coverage_rate",
      key: "coverage_rate",
      align: "center",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.coverage_rate - b.coverage_rate,
      render: (rate) => (
        <Progress
          percent={rate}
          size="small"
          strokeColor={rateColor(rate)}
          format={(p) => `${p}%`}
          style={{ minWidth: 90 }}
        />
      ),
    },
  ];

  const sessionColumns = (round) => [
    {
      title: "วันที่",
      dataIndex: "date",
      key: "date",
      render: (v) => dayjs(v).format("DD/MM/YYYY"),
    },
    {
      title: "สแกน",
      key: "scan",
      align: "center",
      render: (_, s) => (
        <Text>
          {s.scanned}/{s.expected}
        </Text>
      ),
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      render: (_, s) => {
        if (s.in_progress) return <Tag color="processing">กำลังดำเนินการ</Tag>;
        if (s.complete) return <Tag color="green">ครบ</Tag>;
        return <Tag color="red">ขาด {s.missing} จุด</Tag>;
      },
    },
    {
      title: "รปภ. ที่สแกน",
      dataIndex: "guards",
      key: "guards",
      render: (list) =>
        list.length > 0 ? (
          <Space size={4} wrap>
            {list.map((name) => (
              <Tag key={name} icon={<TeamOutlined />}>
                {name}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="danger">ไม่มีใครสแกน</Text>
        ),
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, s) => (
        <Button size="small" onClick={() => openSession(round, s)}>
          รายละเอียด
        </Button>
      ),
    },
  ];

  const sessionDetailColumns = [
    {
      title: "จุดตรวจ",
      dataIndex: "checkpoint_name",
      key: "checkpoint_name",
      render: (name, r) => (
        <Space size={4}>
          <Text>{name}</Text>
          {r.removed_from_round && (
            <Tooltip title="จุดนี้ถูกถอดออกจากรอบแล้วในปัจจุบัน">
              <Tag color="default">นอกรายการปัจจุบัน</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      render: (_, r) => {
        if (!r.scanned) return <Tag color="red">ไม่มีใครสแกน</Tag>;
        const tag = STATUS_TAGS[r.status] || { color: "green", label: "ตรวจแล้ว" };
        return (
          <Space size={4}>
            <Tag color={tag.color}>{tag.label}</Tag>
            {r.is_late && r.status === "completed" && (
              <Tag color="orange">ช้า</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "รปภ. ที่สแกน",
      dataIndex: "guard_name",
      key: "guard_name",
      render: (v) => v || "-",
    },
    {
      title: "เวลาสแกน",
      dataIndex: "time_checked",
      key: "time_checked",
      align: "center",
      render: (v) => (v ? dayjs(v).format("HH:mm:ss") : "-"),
    },
    {
      title: "เหตุผล",
      dataIndex: "reason",
      key: "reason",
      responsive: ["md"],
      render: (v) => v || "-",
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          <TrophyOutlined style={{ marginRight: 8 }} />
          รายงานประสิทธิภาพ รปภ.
        </Title>
        <Space wrap>
          <Select
            allowClear
            placeholder="ทุกบริษัท"
            style={{ minWidth: 200 }}
            value={companyId}
            onChange={setCompanyId}
            showSearch
            optionFilterProp="children"
          >
            {companies.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) =>
              setDateRange(dates || [dayjs().subtract(29, "day"), dayjs()])
            }
            disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
            allowClear={false}
            placeholder={["วันเริ่มต้น", "วันสิ้นสุด"]}
            format="DD/MM/YYYY"
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={refresh}
            loading={activeTab === "guards" ? guardsLoading : roundsLoading}
          >
            รีเฟรช
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={exportExcel}
            loading={exporting}
            style={{ background: "#1D6F42" }}
          >
            Export Excel
          </Button>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "guards",
              label: (
                <span>
                  <TeamOutlined /> รายคน (ประสิทธิภาพ)
                </span>
              ),
              children: (
                <Table
                  rowKey="guard_id"
                  columns={guardColumns}
                  dataSource={guards}
                  loading={guardsLoading}
                  size="middle"
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  onRow={(record) => ({
                    onClick: () => openDetail(record),
                    style: { cursor: "pointer" },
                  })}
                  rowClassName={(record) =>
                    record.rounds_joined > 0 &&
                    record.round_complete_rate < LOW_COMPLETION_THRESHOLD
                      ? "guard-perf-low-row"
                      : ""
                  }
                  locale={{ emptyText: "ไม่พบข้อมูลการตรวจในช่วงเวลาที่เลือก" }}
                />
              ),
            },
            {
              key: "rounds",
              label: (
                <span>
                  <OrderedListOutlined /> รายรอบ (ความครบถ้วน)
                </span>
              ),
              children: (
                <Table
                  rowKey="round_id"
                  columns={roundColumns}
                  dataSource={rounds}
                  loading={roundsLoading}
                  size="middle"
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  rowClassName={(record) =>
                    record.coverage_rate < LOW_COMPLETION_THRESHOLD
                      ? "guard-perf-low-row"
                      : ""
                  }
                  expandable={{
                    expandedRowRender: (round) => (
                      <Table
                        rowKey="date"
                        columns={sessionColumns(round)}
                        dataSource={round.sessions}
                        size="small"
                        pagination={{ pageSize: 10 }}
                      />
                    ),
                  }}
                  locale={{ emptyText: "ไม่พบรอบตรวจในช่วงเวลาที่เลือก" }}
                />
              ),
            },
          ]}
        />
      </Card>
      <style>{`.guard-perf-low-row td { background: #fff1f0 !important; }`}</style>

      {/* ─── Modal: per-guard drill-down ─────────────────────────────── */}
      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={1000}
        title={
          detail ? (
            <Space>
              <span>{detail.guard.guard_name}</span>
              {detail.guard.employee_id && (
                <Text type="secondary">({detail.guard.employee_id})</Text>
              )}
            </Space>
          ) : (
            "รายละเอียด รปภ."
          )
        }
      >
        <Card loading={detailLoading} bordered={false} bodyStyle={{ padding: 0 }}>
          {detail && (
            <>
              <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="บริษัทต้นสังกัด">
                  {detail.guard.home_company_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="ใช้งานล่าสุด">
                  {formatDateTime(detail.guard.last_active)}
                </Descriptions.Item>
                <Descriptions.Item label="บริษัทที่ได้รับมอบหมาย" span={2}>
                  {detail.guard.assigned_companies?.length > 0
                    ? detail.guard.assigned_companies.map((name) => (
                        <Tag key={name}>{name}</Tag>
                      ))
                    : "-"}
                </Descriptions.Item>
              </Descriptions>

              {detailSummary && (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={12} sm={6}>
                    <Statistic title="ทั้งหมด" value={detailSummary.total} />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="ตรวจแล้ว"
                      value={detailSummary.scanned}
                      valueStyle={{ color: "#52c41a" }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="ขาดตรวจ"
                      value={detailSummary.missed}
                      valueStyle={{ color: "#f5222d" }}
                      prefix={<WarningOutlined />}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="ตรวจช้า"
                      value={detailSummary.late}
                      valueStyle={{ color: "#fa8c16" }}
                      prefix={<ClockCircleOutlined />}
                    />
                  </Col>
                </Row>
              )}

              <Title level={5} style={{ marginTop: 0 }}>
                รอบที่ร่วมสแกน (ผลรวมของทีม)
              </Title>
              <Table
                rowKey={(s) => `${s.round_id}|${s.date}`}
                columns={guardSessionColumns}
                dataSource={detail.sessions || []}
                size="small"
                pagination={{ pageSize: 7 }}
                rowClassName={(s) =>
                  !s.in_progress && !s.complete ? "guard-perf-low-row" : ""
                }
                locale={{ emptyText: "ไม่พบข้อมูล" }}
                style={{ marginBottom: 16 }}
              />

              <Title level={5}>ประวัติการสแกนรายจุด</Title>
              <Table
                rowKey="id"
                columns={detailLogColumns}
                dataSource={detail.logs}
                size="small"
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: "ไม่พบข้อมูล" }}
              />
            </>
          )}
        </Card>
      </Modal>

      {/* ─── Modal: round session drill-down ─────────────────────────── */}
      <Modal
        open={sessionOpen}
        onCancel={() => setSessionOpen(false)}
        footer={null}
        width={900}
        title={
          session ? (
            <Space>
              <span>{session.round.name}</span>
              <Text type="secondary">
                {session.round.company_name} ·{" "}
                {dayjs(session.date).format("DD/MM/YYYY")}
              </Text>
            </Space>
          ) : (
            "รายละเอียดรอบตรวจ"
          )
        }
      >
        <Card loading={sessionLoading} bordered={false} bodyStyle={{ padding: 0 }}>
          {session && (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={8}>
                  <Statistic
                    title="จุดทั้งหมด"
                    value={session.checkpoints.filter((c) => !c.removed_from_round).length}
                  />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic
                    title="สแกนแล้ว"
                    value={session.checkpoints.filter((c) => c.scanned && !c.removed_from_round).length}
                    valueStyle={{ color: "#52c41a" }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic
                    title="ไม่มีใครสแกน"
                    value={session.checkpoints.filter((c) => !c.scanned).length}
                    valueStyle={{ color: "#f5222d" }}
                    prefix={<WarningOutlined />}
                  />
                </Col>
              </Row>
              <Table
                rowKey="checkpoint_id"
                columns={sessionDetailColumns}
                dataSource={session.checkpoints}
                size="small"
                pagination={false}
                rowClassName={(r) => (!r.scanned ? "guard-perf-low-row" : "")}
                locale={{ emptyText: "ไม่พบข้อมูล" }}
              />
            </>
          )}
        </Card>
      </Modal>
    </div>
  );
};

export default GuardPerformance;
